import { useEffect, useState } from "react";
import { mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  coerceMaterialsEffect,
  normalizeMaterialsEffect,
  type NamedVariantDeltaLine,
  type NamedVariantMaterialsEffect,
  type NamedVariantMaterialsEffectMode,
} from "@/lib/manufacturing/namedVariantMaterialsEffect";
import { getManufacturingRecipeDetail } from "@/lib/manufacturing/recipesClient";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import type { VariantDeltaLine } from "@/lib/manufacturing/variantConfig";
import { ManufacturingNamedVariantApplicationTransformRules } from "./ManufacturingNamedVariantApplicationTransformRules";
import { ManufacturingNamedVariantDeltaLinesEditor } from "./ManufacturingNamedVariantDeltaLinesEditor";
import type { RecipeLineMaterialSource } from "./namedVariantRecipeMaterialOptions";

function isComponentDelta(ln: NamedVariantDeltaLine): boolean {
  return Boolean(ln.component_recipe_id || ln.component_recipe_code?.trim());
}

function toEditorLines(lines: readonly NamedVariantDeltaLine[]): VariantDeltaLine[] {
  return lines.filter((ln) => !isComponentDelta(ln)).map((ln) => ({
    material_id: ln.material_id ?? null,
    material_use_id: ln.material_use_id ?? null,
    material_code: ln.material_code ?? "",
    material_name: ln.material_name ?? "",
    quantity: ln.quantity,
    slot_label: ln.slot_label ?? "",
  }));
}

function fromEditorLines(lines: readonly VariantDeltaLine[]): NamedVariantDeltaLine[] {
  return lines.map((ln) => ({
    material_id: ln.material_id,
    material_use_id: ln.material_use_id,
    material_code: ln.material_code || undefined,
    material_name: ln.material_name || undefined,
    quantity: ln.quantity,
    slot_label: ln.slot_label || undefined,
    role: "structural",
  }));
}

function modeLabel(mode: NamedVariantMaterialsEffectMode): string {
  if (mode === "hybrid") return "Hybrid (transform + delta)";
  if (mode === "transform") return "Transform";
  return "Delta";
}

/** Align a saved application effect to the variant template mode (authoritative). */
function draftFromEffect(
  effect: NamedVariantMaterialsEffect,
  templateMode: NamedVariantMaterialsEffectMode,
): NamedVariantMaterialsEffect {
  const base = coerceMaterialsEffect(effect);
  const match = base.match;
  if (templateMode === "delta") {
    return { mode: "delta", delta_lines: base.delta_lines ?? effect.delta_lines, match };
  }
  if (templateMode === "transform") {
    return {
      mode: "transform",
      transform_rules: base.transform_rules ?? effect.transform_rules,
      replaces_slot_labels: base.replaces_slot_labels,
      match,
    };
  }
  return coerceMaterialsEffect({
    mode: "hybrid",
    transform_rules: base.transform_rules ?? effect.transform_rules,
    delta_lines: base.delta_lines ?? effect.delta_lines,
    replaces_slot_labels: base.replaces_slot_labels ?? effect.replaces_slot_labels,
    match,
  });
}

/** Editable materials_effect for a pending named-variant application. Mode is locked to the variant template. */
export function ManufacturingNamedVariantApplicationEffectEditor({
  effect,
  templateMode,
  parentRecipeId,
  materials,
  orgSlotLabels,
  saveBusy,
  onSave,
  namedVariantId,
  onError,
}: {
  effect: NamedVariantMaterialsEffect;
  /** Authoritative mode from the named-variant default template. */
  templateMode: NamedVariantMaterialsEffectMode;
  parentRecipeId: string;
  materials: readonly MfgMaterial[];
  orgSlotLabels: readonly string[];
  saveBusy: boolean;
  onSave: (next: NamedVariantMaterialsEffect) => void;
  namedVariantId?: string | null;
  onError?: (message: string) => void;
}) {
  const [draft, setDraft] = useState(() => draftFromEffect(effect, templateMode));
  const [recipeLines, setRecipeLines] = useState<RecipeLineMaterialSource[]>([]);
  const [recipeLinesBusy, setRecipeLinesBusy] = useState(false);
  const mode = templateMode;

  useEffect(() => {
    setDraft(draftFromEffect(effect, templateMode));
  }, [effect, templateMode]);

  useEffect(() => {
    let cancelled = false;
    setRecipeLinesBusy(true);
    void getManufacturingRecipeDetail(parentRecipeId).then((r) => {
      if (cancelled) return;
      setRecipeLinesBusy(false);
      if (!r.ok) {
        setRecipeLines([]);
        return;
      }
      setRecipeLines(
        r.data.lines.map((ln) => ({
          line_kind: ln.line_kind,
          material_code: ln.material_code,
          material_name: ln.material_name,
          slot_label: ln.slot_label,
        })),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [parentRecipeId]);

  return (
    <div className="space-y-3 rounded-md border border-gray-100 p-2 dark:border-zinc-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
          Materials effect for this recipe (does not change the variant template)
        </span>
        <span className="text-[11px] text-gray-500 dark:text-zinc-400">{modeLabel(mode)}</span>
      </div>

      {mode === "transform" || mode === "hybrid" ? (
        <ManufacturingNamedVariantApplicationTransformRules
          rules={draft.transform_rules ?? []}
          recipeLines={recipeLines}
          recipeLinesBusy={recipeLinesBusy}
          materials={materials}
          orgSlotLabels={orgSlotLabels}
          onChange={(transform_rules) =>
            setDraft((d) => ({
              ...d,
              mode,
              transform_rules,
            }))
          }
        />
      ) : null}

      {mode === "delta" || mode === "hybrid" ? (
        <ManufacturingNamedVariantDeltaLinesEditor
          lines={toEditorLines(draft.delta_lines ?? [])}
          materials={materials}
          orgSlotLabels={orgSlotLabels}
          namedVariantId={namedVariantId}
          onError={onError}
          onChange={(delta_lines) =>
            setDraft((d) => ({
              ...d,
              mode,
              delta_lines: [
                ...fromEditorLines(delta_lines),
                ...(d.delta_lines ?? []).filter(isComponentDelta),
              ],
            }))
          }
        />
      ) : null}

      <button
        type="button"
        className={mfgPrimaryBtnClass}
        disabled={saveBusy}
        onClick={() =>
          onSave(
            normalizeMaterialsEffect(
              coerceMaterialsEffect(
                draftFromEffect(
                  {
                    mode,
                    transform_rules: draft.transform_rules,
                    delta_lines: draft.delta_lines,
                    replaces_slot_labels: draft.replaces_slot_labels,
                  },
                  mode,
                ),
              ),
            ),
          )
        }
      >
        {saveBusy ? "Saving…" : "Save materials effect"}
      </button>
    </div>
  );
}
